import { Fragment, ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";

import { Product } from "../models/product.model";
import { useProductsApi } from "../../../../common/hooks/use-products-api.hook";
import { resolveApplicationTypeLabel, dedupeApplicationTypes } from "../../../../common/utils/application-type";

/** One label/value line of the spec table; there are seven of them. */
const DetailRow = ({ label, value }: { label: ReactNode; value: ReactNode }) => (
    <div className="flex justify-between gap-4">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <span className="text-end text-xs text-foreground">{value}</span>
    </div>
);

export const ProductView = ({selectedProductForViewID, setSelectedProductForView, anchorEl, setAnchorEl}) => {
    const [product, setProduct] = useState<Product>()
    const [loading, setLoading] = useState(false)
    const [popoverPosition, setPopoverPosition] = useState<'left' | 'right'>('right')
    const productsAPI = useProductsApi()
    const [t] = useTranslation();

    useEffect(() => {
        const getProduct = async () => {
            setLoading(true)
            const result = await productsAPI.findOneProduct(selectedProductForViewID)
            setProduct(result)
            setLoading(false)
        }
        getProduct()
    }, [selectedProductForViewID])

    useEffect(() => {
        if (anchorEl) {
            const rect = anchorEl.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const popoverWidth = 650;

            // If the anchor is on the right half of the screen and there's not enough space on the right
            if (rect.right > viewportWidth / 2 && (viewportWidth - rect.right) < popoverWidth) {
                setPopoverPosition('left');
            } else {
                setPopoverPosition('right');
            }
        }
    }, [anchorEl]);

    const handleClose = () => {
        setAnchorEl(null);
        setSelectedProductForView(null);
    };

    return (
        <Fragment>
            <Popover
                open={Boolean(anchorEl)}
                onOpenChange={(open) => {
                    if (!open) handleClose();
                }}
            >
                {/*
                  The anchor is a DOM node the catalogue hands us, not a trigger we
                  render, so it is fed to Radix as a virtual anchor. `virtualRef`
                  renders nothing of its own.
                */}
                <PopoverAnchor virtualRef={{ current: anchorEl }} />
                <PopoverContent
                    side={popoverPosition}
                    align="center"
                    sideOffset={12}
                    className="max-h-[90vh] w-[650px] gap-0 overflow-hidden p-0"
                >
                    {loading ? (
                        <div className="flex h-[300px] w-full items-center justify-center">
                            <Spinner className="size-8 text-muted-foreground" />
                        </div>
                    ) : product && (
                        <div className="flex max-h-[90vh] flex-col">
                            {/* Header */}
                            <div className="flex items-start justify-between gap-4 border-b p-5">
                                <div className="min-w-0 flex-1">
                                    <p className="font-heading text-base leading-snug font-medium tracking-tight break-words text-foreground">
                                        {product.name}
                                    </p>
                                    <p className="mt-1 text-xs text-muted-foreground">
                                        {product.color} · {product.sizeName} · {product.pattern}
                                    </p>
                                </div>
                                <Button
                                    variant="outline"
                                    size="icon-sm"
                                    onClick={handleClose}
                                    aria-label={t('COMMON.CLOSE') as string}
                                    className="shrink-0"
                                >
                                    <X />
                                </Button>
                            </div>

                            {/* Content - Side by Side Layout */}
                            <div className="flex flex-1 gap-5 overflow-hidden p-5">
                                {/* Left Side - Product Image & Variants */}
                                <div className="flex w-50 shrink-0 flex-col gap-4">
                                    <div className="overflow-hidden border">
                                        <img
                                            src={product.thumbnailPath}
                                            className="block aspect-square w-full object-cover"
                                            alt={product.name}
                                        />
                                    </div>

                                    {/* Variants */}
                                    {product.siblings && product.siblings.length > 0 && (
                                        <div>
                                            <p className="mb-2.5 text-xs font-medium text-muted-foreground">
                                                {t('PRODUCT_DETAILS.VARIANTS')} ({product.siblings.length})
                                            </p>
                                            <div className="flex flex-wrap gap-2">
                                                {product.siblings.map(sibling => (
                                                    <button
                                                        type="button"
                                                        key={sibling.id}
                                                        onClick={() => {
                                                            setSelectedProductForView(sibling.uuid);
                                                        }}
                                                        className="size-12 overflow-hidden border transition-transform hover:scale-105"
                                                    >
                                                        <img
                                                            src={sibling.thumbnailPath}
                                                            className="block size-full object-cover"
                                                            alt={sibling.name}
                                                        />
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Right Side - Product Details */}
                                <div className="flex-1 overflow-y-auto pe-1">

                                    {/* Description */}
                                    {product.description && (
                                        <p className="mb-5 text-xs leading-relaxed text-muted-foreground">
                                            {product.description}
                                        </p>
                                    )}

                                    {/* Product Details */}
                                    <div className="mb-5 grid gap-2.5 border bg-muted p-4">
                                        <DetailRow label={t('PRODUCT_DETAILS.BRAND')} value={product.brand} />
                                        <DetailRow label={t('PRODUCT_DETAILS.COLOR')} value={product.color} />
                                        <DetailRow label={t('PRODUCT_DETAILS.PATTERN')} value={product.pattern} />
                                        <DetailRow label={t('PRODUCT_DETAILS.SIZE')} value={product.sizeName} />
                                        <DetailRow
                                            label={t('PRODUCT_DETAILS.SURFACE')}
                                            value={dedupeApplicationTypes(product.application_types).map(type => resolveApplicationTypeLabel(t, type)).join(', ')}
                                        />
                                        <DetailRow
                                            label={t('PRODUCT_DETAILS.TYPE')}
                                            value={t('TYPES.' + product.type.toUpperCase(), { defaultValue: product.type })}
                                        />
                                        <DetailRow label={t('PRODUCT_DETAILS.SKU')} value={product.sku} />
                                    </div>

                                    {/* Link Button */}
                                    {product.link && (
                                        <Button
                                            variant="outline"
                                            size="lg"
                                            onClick={() => window.open(product.link, '_blank')}
                                            className="mt-5 w-full"
                                        >
                                            <ExternalLink data-icon="inline-start" />
                                            {t('PRODUCT_DETAILS.VIEW_PRODUCT_PAGE')}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </PopoverContent>
            </Popover>
        </Fragment>
    );
}
